import { useCampaignStore } from "@entities/global-discovery/store/useCampaignStore";
import { IBrief } from "@shared/lib/types/brief";
import { useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { insightsiqPlatformWorkIdMap } from "../constants";
import { useGetGlobalDiscoveryStore } from "../store/useGetGlobalDiscoveryStoreV2";
import {
  deserializeFiltersFromUrl,
  serializeFiltersToUrl
} from "../utils/urlSync";
import { useGlobalDiscoveryFacets } from "./useGlobalDiscoveryFacets";

/**
 * Hook to sync URL search params with global discovery store
 * - Initializes store from URL on mount
 * - Updates URL when filters/pagination change
 * - Triggers fetchData when needed
 */
export const useGlobalDiscoveryUrlSync = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const isInitialMount = useRef(true);

  const { setCampaign } = useCampaignStore();

  const campaignKeyFromSearchParams = searchParams.get("campaignKey");
  const campaignFromSearchParams: IBrief | null = (() => {
    try {
      if (campaignKeyFromSearchParams) {
        const raw = sessionStorage.getItem(campaignKeyFromSearchParams);
        if (raw) return JSON.parse(raw) as IBrief;
      }
      const encoded = searchParams.get("campaign");
      if (encoded) {
        try {
          return JSON.parse(encoded) as IBrief;
        } catch (_e) {
          try {
            return JSON.parse(atob(encoded)) as IBrief;
          } catch (_e2) {
            return null;
          }
        }
      }
      return null;
    } catch (_err) {
      return null;
    }
  })();

  const {
    backendFilters,
    platformId,
    currentPage,
    pageSize,
    setFiltersFromUrl,
    setPlatform,
    setCurrentPage,
    setPageSize,
    saveFilters,
    fetchData,
    reset,
  } = useGetGlobalDiscoveryStore();

  const hasSearchParams = Array.from(searchParams.keys()).length > 0;

  const urlData = deserializeFiltersFromUrl(searchParams);
  const initialPlatformId = urlData?.platformId || insightsiqPlatformWorkIdMap["instagram"];
  const filtersFromUrl = urlData?.filters || {};

  const { facet, isFetching } = useGlobalDiscoveryFacets({
    facetName: "creator_locations",
    query: "India",
    work_platform_id: initialPlatformId,
    body: {},
    enabled: !hasSearchParams,
  });


  // Initialize store from URL on mount, or reset to defaults when re-visiting/clearing searchParams
  useEffect(() => {
    if (!isInitialMount.current) {
      return;
    }

    // If we need the default location, wait until the facet is fetched
    if (!hasSearchParams && isFetching) {
      return;
    }

    const filters = { ...filtersFromUrl };

    if (!hasSearchParams && facet) {
      const indiaFacet = facet.find(
        (item: any) =>
          item.name?.toLowerCase() === "india" ||
          item.display_name?.toLowerCase() === "india"
      );
      if (indiaFacet) {
        filters.creator_locations = [indiaFacet.value];
        if (!filters.filterTags) {
          filters.filterTags = {};
        }
        filters.filterTags.creator_locations = [indiaFacet.name || "India"];
      }
    }

    if (!hasSearchParams) {
      if (!filters.follower_count) {
        filters.follower_count = { min: 10000, max: 100000 };
        if (!filters.filterTags) {
          filters.filterTags = {};
        }
        filters.filterTags.follower_count = "10k - 100k";
      }
    }

    setFiltersFromUrl(
      filters,
      initialPlatformId,
      urlData?.currentPage || 1
    );
    if (campaignFromSearchParams) {
      setCampaign(campaignFromSearchParams, campaignFromSearchParams?._id);
    }
    if (urlData.pageSize !== 20) {
      setPageSize(urlData.pageSize);
    }

    // Fetch data with URL filters
    fetchData();

    isInitialMount.current = false;
  }, [isFetching, hasSearchParams, searchParams]); // Run when facet, needDefaultLocation, or searchParams change

  // Update URL when backendFilters, pagination, or platform changes
  useEffect(() => {
    // Skip on initial mount (handled above)
    if (isInitialMount.current) {
      return;
    }

    const newParams = serializeFiltersToUrl(
      backendFilters,
      platformId,
      currentPage,
      pageSize
    );

    // Only update if params actually changed
    const currentParamsString = searchParams.toString();
    const newParamsString = newParams.toString();

    if (currentParamsString !== newParamsString) {
      setSearchParams(newParams, { replace: true });
    }
  }, [
    backendFilters,
    platformId,
    currentPage,
    pageSize,
    searchParams,
    setSearchParams,
  ]);

  // Handle filter changes - when selectedFilters change and user saves
  const handleSaveFilters = () => {
    saveFilters(); // This will update backendFilters and trigger fetchData
    // URL will be updated by the useEffect watching backendFilters
  };

  // Handle pagination changes
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    fetchData();
    // URL will be updated by the useEffect watching currentPage
  };

  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize);
    fetchData();
    // URL will be updated by the useEffect watching pageSize
  };

  // Handle platform change
  const handlePlatformChange = (newPlatformId: string) => {
    reset();
    setPlatform(newPlatformId);
    fetchData();
  };

  return {
    handleSaveFilters,
    handlePageChange,
    handlePageSizeChange,
    handlePlatformChange,
  };
};
